import { useMemo, useState, useEffect, useRef } from "react";
import { format as dateFormat } from "date-fns";
import { ContentCopy, CheckCircle } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import { useSnackbar } from "notistack";
import { TextField, Button, Modal, Box, IconButton, Tooltip, InputAdornment } from "@mui/material";
import DataTable from "../Table/Datatable";
import { DOMAIN_API_HOST } from "../../../configs/main";
import "./AgencyClients.scss";

const PHONE_MASK = "+7 (___) ___-__-__";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const cleanDigits = (value) => value.replace(/\D/g, "");

const formatRussianPhone = (value) => {
  const digits = cleanDigits(value);
  if (!digits) return "";
  let normalized = digits;
  if (normalized[0] === "8") {
    normalized = "7" + normalized.slice(1);
  }
  if (normalized[0] !== "7") {
    normalized = "7" + normalized;
  }
  normalized = normalized.slice(0, 11);

  const rest = normalized.slice(1);
  let result = "+7";

  if (rest.length) {
    result += " (" + rest.slice(0, Math.min(3, rest.length));
    if (rest.length >= 3) {
      result += ")";
    }
  }
  if (rest.length > 3) {
    result += " " + rest.slice(3, Math.min(6, rest.length));
  }
  if (rest.length > 6) {
    result += "-" + rest.slice(6, Math.min(8, rest.length));
  }
  if (rest.length > 8) {
    result += "-" + rest.slice(8, Math.min(10, rest.length));
  }

  return result;
};

const isValidPhone = (value) => {
  const digits = cleanDigits(value);
  return digits.length === 11 && digits.startsWith("7");
};

const validateField = (field, value) => {
  switch (field) {
    case "first_name": {
      const trimmed = value.trim();
      if (!trimmed) return "Обязательное поле";
      return /^[а-яА-Я\s-]+$/.test(trimmed) ? null : "Допустимы символы русского, пробелы и дефисы";
    }
    case "surname": {
      const trimmed = value.trim();
      if (!trimmed) return "Обязательное поле";
      return /^[а-яА-Я\s-]+$/.test(trimmed) ? null : "Допустимы символы русского, пробелы и дефисы";
    }
    case "email": {
      const trimmed = value.trim();
      if (!trimmed) return "Обязательное поле";
      return emailRegex.test(trimmed) ? null : "Некорректная почта";
    }
    case "phone_number":
      if (!value.trim()) return "Обязательное поле";
      return isValidPhone(value) ? null : "Введите номер в формате +7 (XXX) XXX-XX-XX";
    case "inn": {
      const digits = cleanDigits(value);
      if (!digits) return "Обязательное поле";
      return [10, 12].includes(digits.length) ? null : "ИНН должен содержать 10 или 12 цифр";
    }
    default:
      return null;
  }
};


const getToken = () => {
  return localStorage.getItem("agency_token");
};

const createAgencyUser = async (data) => {
  const token = getToken();
  const response = await fetch(`${DOMAIN_API_HOST}/api/v1/agency/users`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: "Ошибка при создании клиента" }));
    throw new Error(errorData.detail || "Ошибка при создании клиента");
  }

  return response.json();
};

const fetchAgencyUsers = async (params) => {
  const token = getToken();
  const queryParams = new URLSearchParams();

  if (params.page !== undefined) queryParams.append("page", params.page);
  if (params.limit !== undefined) queryParams.append("limit", params.limit);
  if (params.search) queryParams.append("search", params.search);
  if (params.agency) queryParams.append("agency", params.agency);

  const response = await fetch(`${DOMAIN_API_HOST}/api/v1/agency/users?${queryParams.toString()}`, {
    method: "GET",
    headers: {
      "Authorization": `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: "Ошибка при загрузке клиентов" }));
    throw new Error(errorData.detail || "Ошибка при загрузке клиентов");
  }

  return response.json();
};

const fetchAgencyBalance = async () => {
  const token = getToken();
  const response = await fetch(`${DOMAIN_API_HOST}/api/v1/agency/balance-summary`, {
    method: "GET",
    headers: {
      "Authorization": `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: "Ошибка при загрузке баланса" }));
    throw new Error(errorData.detail || "Ошибка при загрузке баланса");
  }

  return response.json();
};

const CONTRACT_TYPE_LABELS = {
  service_agreement: "Договор оказания услуг",
  mediation_agreement: "Посреднический договор",
  additional_agreement: "Доп. соглашение",
};

const formatContractAmount = (amount) =>
  amount != null ? `${amount.toLocaleString("ru-RU")} ₽` : "—";

const formatContractDate = (value) =>
  value ? dateFormat(new Date(value), "dd/MM/yyyy") : "—";

const formatContractDateTime = (value) =>
  value ? dateFormat(new Date(value), "dd/MM/yyyy HH:mm") : "—";

const fetchAgencyClientContracts = async (clientId) => {
  const token = getToken();
  const queryParams = new URLSearchParams();
  queryParams.append("client_id", clientId);

  const response = await fetch(
    `${DOMAIN_API_HOST}/api/v1/agency/clients/contracts?${queryParams.toString()}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: "Ошибка при загрузке договоров" }));
    throw new Error(errorData.detail || "Ошибка при загрузке договоров");
  }

  return response.json();
};

const transferToClient = async (clientId, amount) => {
  const token = getToken();
  const queryParams = new URLSearchParams();
  queryParams.append("client_id", clientId);

  const response = await fetch(`${DOMAIN_API_HOST}/api/v1/agency/transfer?${queryParams.toString()}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
    },
    body: JSON.stringify({ amount }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: "Ошибка при переводе средств" }));
    throw new Error(errorData.detail || "Ошибка при переводе средств");
  }

  return response.json();
};

const columns = [
  { Header: "Перейти в ЛК", accessor: "redirect", width: 120, minWidth: 80, maxWidth: 400, isSticky: true, },
  { Header: "ID клиента", accessor: "id", width: 130, minWidth: 80, maxWidth: 400 },
  { Header: "ФИ клиента", accessor: "full_name", width: 200, minWidth: 80, maxWidth: 400 },
  { Header: "Логин (email)", accessor: "email", width: 220, minWidth: 80, maxWidth: 400, isSticky: true },
  { Header: "Номер телефона", accessor: "phone_number", width: 170, minWidth: 80, maxWidth: 400 },
  { Header: "Название юр.лица", accessor: "entity_full_name", width: 200, minWidth: 80, maxWidth: 400 },
  { Header: "Сайт", accessor: "site", width: 250, minWidth: 80, maxWidth: 400 },
  { Header: "ИНН", accessor: "inn", width: 120, minWidth: 80, maxWidth: 400 },
  { Header: "РК", accessor: "campaigns_number", width: 100, minWidth: 50, maxWidth: 400 },
  { Header: "Дата регистр.", accessor: "created_at", width: 130, minWidth: 80, maxWidth: 400 },
  { Header: "Подтв.", accessor: "is_email_confirmed", width: 50, minWidth: 50, maxWidth: 400 },
  { Header: "Баланс", accessor: "balance_score", width: 118, minWidth: 80, maxWidth: 400 }, // Тут добавить иконку карандаша для пополнения
  { Header: "Сумма пополнения.", accessor: "deposits_amount", width: 130, minWidth: 80, maxWidth: 400 },
  { Header: "Дата посл. пополн.", accessor: "last_deposit_at", width: 130, minWidth: 80, maxWidth: 400 },
  { Header: "Договоры", accessor: "contracts", width: 120, minWidth: 80, maxWidth: 400 },
];

function AgencyClients() {
  const { enqueueSnackbar } = useSnackbar();
  const ITEMS_PER_PAGE = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [newClient, setNewClient] = useState({
    first_name: "",
    surname: "",
    email: "",
    phone_number: "",
    inn: "",
  });
  const [createdClientInfo, setCreatedClientInfo] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [debouncedValues, setDebouncedValues] = useState({});
  const [copiedFields, setCopiedFields] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [transferModal, setTransferModal] = useState(false);
  const [currentClient, setCurrentClient] = useState(null);
  const [transferAmount, setTransferAmount] = useState("");
  const [transferError, setTransferError] = useState("");
  const [agencyBalance, setAgencyBalance] = useState(0);
  const [contractsModal, setContractsModal] = useState(false);
  const [contractsClient, setContractsClient] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [contractsLoading, setContractsLoading] = useState(false);
  const debounceTimers = useRef({});
  const searchDebounceTimer = useRef(null);

  useEffect(() => {
    if (searchDebounceTimer.current) {
      clearTimeout(searchDebounceTimer.current);
    }
    searchDebounceTimer.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setCurrentPage(1);
    }, 600);

    return () => {
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }
    };
  }, [searchQuery]);

  useEffect(() => {
    const loadAgencyBalance = async () => {
      try {
        const data = await fetchAgencyBalance();
        setAgencyBalance(data.agency_balance || 0);
      } catch (error) {
        enqueueSnackbar(error.message || "Ошибка при загрузке баланса", {
          variant: "error",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
      }
    };

    loadAgencyBalance();
  }, [enqueueSnackbar]);

  useEffect(() => {
    const loadClients = async () => {
      setIsLoading(true);
      try {
        const params = {
          page: currentPage,
          limit: ITEMS_PER_PAGE,
        };

        if (debouncedSearchQuery.trim()) {
          params.search = debouncedSearchQuery.trim();
        }

        const response = await fetchAgencyUsers(params);
        const data = Array.isArray(response.data) ? response.data : [];

        setClients(data);
        setTotalPages(Math.max(1, response.total_pages || 1));
      } catch (error) {
        enqueueSnackbar(error.message || "Ошибка при загрузке клиентов", {
          variant: "error",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
        setClients([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadClients();
  }, [currentPage, debouncedSearchQuery, enqueueSnackbar]);

  const formatPhoneNumber = (phone) => {
    if (!phone) return PHONE_MASK;
    const digits = cleanDigits(phone);
    if (digits.length === 11 && digits.startsWith("7")) {
      return formatRussianPhone(phone);
    }
    return phone;
  };

  const handleOpenContracts = async (client) => {
    setContractsClient(client);
    setContractsModal(true);
    setContracts([]);
    setContractsLoading(true);

    try {
      const response = await fetchAgencyClientContracts(client.id);
      setContracts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      enqueueSnackbar(error.message || "Ошибка при загрузке договоров", {
        variant: "error",
        autoHideDuration: 3000,
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
    } finally {
      setContractsLoading(false);
    }
  };

  const handleCloseContracts = () => {
    setContractsModal(false);
    setContractsClient(null);
    setContracts([]);
    setContractsLoading(false);
  };

  const handleOpenTransfer = (client) => {
    setCurrentClient(client);
    setTransferModal(true);
    setTransferAmount("");
    setTransferError("");
  };

  const handleCloseTransfer = () => {
    setTransferModal(false);
    setCurrentClient(null);
    setTransferAmount("");
    setTransferError("");
  };

  const handleTransfer = async () => {
    if (!transferAmount || !transferAmount.trim()) {
      setTransferError("Введите сумму пополнения");
      return;
    }

    const amount = Number(transferAmount);
    if (isNaN(amount) || amount <= 0) {
      setTransferError("Сумма должна быть больше 0");
      return;
    }

    if (amount > agencyBalance) {
      setTransferError(`Недостаточно средств. Доступно: ${agencyBalance.toLocaleString("ru-RU")} ₽`);
      return;
    }

    try {
      await transferToClient(currentClient.id, amount);

      enqueueSnackbar("Баланс клиента пополнен", {
        variant: "success",
        autoHideDuration: 3000,
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });

      const balanceData = await fetchAgencyBalance();
      setAgencyBalance(balanceData.agency_balance || 0);

      const params = {
        page: currentPage,
        limit: ITEMS_PER_PAGE,
      };
      if (debouncedSearchQuery.trim()) {
        params.search = debouncedSearchQuery.trim();
      }
      const reloadResponse = await fetchAgencyUsers(params);
      const reloadData = Array.isArray(reloadResponse.data) ? reloadResponse.data : [];
      setClients(reloadData);

      handleCloseTransfer();
    } catch (error) {
      const errorMessage = error.message || "Ошибка при переводе средств";
      setTransferError(errorMessage);
      enqueueSnackbar(errorMessage, {
        variant: "error",
        autoHideDuration: 4000,
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
    }
  };

  const tableData = useMemo(
    () =>
      clients.map((client) => {
        const fullName = client.full_name || "-";

        const phoneNumber = client.phone_number || "";
        const inn = client.inn || "-";
        const balance = client.deposits_amount || 0;
        const depositsAmount = client.charges_amount || 0;
        const lastDepositAt = client.last_charge_at || null;
        const entityFullName = client.is_entity && client.entity_full_name
          ? client.entity_full_name
          : "-";

        return {
          redirect: (
            <div className="td">
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleAccessToken(client.id)}
              >
                Перейти
              </Button>
            </div>
          ),
          id: (
            <div className="td" style={{ display: "flex", alignItems: "center", gap: 4 }}>
              {client.id}
              <ContentCopy
                fontSize="small"
                className="copyIcon"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  navigator.clipboard.writeText(String(client.id));
                  enqueueSnackbar("ID клиента скопирован", {
                    variant: "success",
                    autoHideDuration: 1000,
                    anchorOrigin: { vertical: "top", horizontal: "right" },
                  });
                }}
              />
            </div>
          ),
          full_name: <div className="td">{fullName}</div>,
          email: <div className="td">{client.email}</div>,
          phone_number: <div className="td">{formatPhoneNumber(phoneNumber)}</div>,
          is_entity: <div className="td">{client.is_entity ? "Юр. лицо" : "Физ. лицо"}</div>,
          entity_full_name: <div className="td">{entityFullName}</div>,
          site: <div className="td">{client.site || "-"}</div>,
          inn: <div className="td">{inn}</div>,
          campaigns_number: <div className="td">{client.campaigns_number || 0}</div>,
          created_at: (
            <div className="td">
              {client.created_at ? dateFormat(new Date(client.created_at), "dd/MM/yyyy") : "-"}
            </div>
          ),
          is_email_confirmed: <div className="td">{client.is_email_confirmed ? "+" : "-"}</div>,
          balance_score: (
            <div className="td" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {balance.toLocaleString("ru-RU")}
              <svg
                onClick={() => handleOpenTransfer(client)}
                style={{ cursor: "pointer" }}
                width="13"
                height="12"
                viewBox="0 0 13 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9.49968 1.25391C9.051 1.25391 8.60238 1.424 8.26237 1.76416L2.72868 7.29736C2.62369 7.40236 2.54679 7.53293 2.50603 7.67627L1.76433 10.2734C1.74603 10.3377 1.74524 10.4057 1.76204 10.4704C1.77883 10.535 1.8126 10.594 1.85985 10.6413C1.90709 10.6885 1.96611 10.7223 2.03078 10.7391C2.09545 10.7559 2.16344 10.7551 2.22771 10.7368L4.82536 9.99512L4.82585 9.99463C4.96823 9.9537 5.09848 9.87776 5.20378 9.77246L10.737 4.23877C11.4173 3.55849 11.4173 2.44444 10.737 1.76416C10.397 1.424 9.94835 1.25391 9.49968 1.25391ZM9.49968 1.99951C9.75488 1.99951 10.0097 2.09784 10.2062 2.29443H10.2067C10.6004 2.68815 10.6004 3.31478 10.2067 3.7085L9.72185 4.19336L8.30778 2.7793L8.79265 2.29443C8.98914 2.09784 9.24448 1.99951 9.49968 1.99951ZM7.77751 3.30957L9.19157 4.72363L4.67302 9.24219C4.65836 9.25675 4.6398 9.2679 4.61882 9.27393L2.67058 9.83057L3.22722 7.88184V7.88135C3.23295 7.86119 3.24345 7.84314 3.25896 7.82764L7.77751 3.30957Z"
                  fill="black"
                />
              </svg>
            </div>
          ),
          deposits_amount: <div className="td">{depositsAmount.toLocaleString("ru-RU")}</div>,
          last_deposit_at: (
            <div className="td">
              {lastDepositAt ? dateFormat(new Date(lastDepositAt), "dd/MM/yyyy") : "-"}
            </div>
          ),
          contracts: (
            <div className="td">
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleOpenContracts(client)}
              >
                Открыть
              </Button>
            </div>
          ),
        };
      }),
    [clients, enqueueSnackbar]
  );

  const handleAccessToken = async (user_id) => {
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append(
      "Authorization",
      "Bearer " + getToken()
    );
    const response = await fetch(`${DOMAIN_API_HOST}/api/v1/admin/auth/access-token/${user_id}`, {
      method: "get",
      headers: myHeaders
    });
    const data = await response.json();
    localStorage.setItem("token", data.access_token);
    return window.open("/");
  };

  const handlePageChange = (direction) => {
    if (direction === "prev" && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
    if (direction === "next" && currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const resetForm = () => {
    setNewClient({
      first_name: "",
      surname: "",
      email: "",
      phone_number: "",
      inn: "",
    });
    setFormErrors({});
    setTouchedFields({});
    setDebouncedValues({});
    setFormSubmitted(false);
    Object.values(debounceTimers.current).forEach(timer => clearTimeout(timer));
    debounceTimers.current = {};
  };

  useEffect(() => {
    Object.keys(debouncedValues).forEach((field) => {
      const value = debouncedValues[field];
      const error = validateField(field, value);
      const isRequiredError = error === "Обязательное поле";

      if (error) {
        if (isRequiredError && !formSubmitted) {
          setFormErrors((prev) => {
            const next = { ...prev };
            delete next[field];
            return next;
          });
        } else {
          setFormErrors((prev) => {
            const next = { ...prev };
            next[field] = error;
            return next;
          });
        }
      } else {
        setFormErrors((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }
    });
  }, [debouncedValues, formSubmitted]);

  const updateField = (field, value) => {
    setNewClient((prev) => ({ ...prev, [field]: value }));

    if (debounceTimers.current[field]) {
      clearTimeout(debounceTimers.current[field]);
    }

    debounceTimers.current[field] = setTimeout(() => {
      setDebouncedValues((prev) => ({ ...prev, [field]: value }));
    }, 500);
  };

  const handleBlur = (field) => {
    setTouchedFields((prev) => ({ ...prev, [field]: true }));
    setDebouncedValues((prev) => ({ ...prev, [field]: newClient[field] }));
  };

  const closeModal = () => {
    setOpenModal(false);
    resetForm();
  };

  useEffect(() => {
    return () => {
      Object.values(debounceTimers.current).forEach(timer => clearTimeout(timer));
    };
  }, []);

  const validateForm = () => {
    const errors = {};
    (["first_name", "surname", "email", "phone_number", "inn"]).forEach((field) => {
      const error = validateField(field, newClient[field]);
      if (error) errors[field] = error;
    });
    setFormErrors(errors);
    setFormSubmitted(true);
    return Object.keys(errors).length === 0;
  };

  const handleCreateClient = async () => {
    setFormSubmitted(true);
    if (!validateForm()) return;

    try {
      const phoneDigits = cleanDigits(newClient.phone_number);
      const innDigits = cleanDigits(newClient.inn);

      const requestData = {
        email: newClient.email.trim(),
        phone: phoneDigits,
        first_name: newClient.first_name.trim(),
        surname: newClient.surname.trim(),
        inn_or_kpp: innDigits || undefined,
        is_entity: !!innDigits,
      };

      const response = await createAgencyUser(requestData);

      const baseUrl = window.location.origin;
      const referralLink = `${baseUrl}/sign-in`;

      setCreatedClientInfo({
        email: response.email,
        password: response.password,
        link: referralLink,
      });
      setSuccessModalOpen(true);
      enqueueSnackbar("Клиент создан", {
        variant: "success",
        autoHideDuration: 1500,
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
      closeModal();

      setCurrentPage(1);
      const params = {
        page: 1,
        limit: ITEMS_PER_PAGE,
      };
      if (debouncedSearchQuery.trim()) {
        params.search = debouncedSearchQuery.trim();
      }
      const reloadResponse = await fetchAgencyUsers(params);
      const reloadData = Array.isArray(reloadResponse.data) ? reloadResponse.data : [];
      setClients(reloadData);
    } catch (error) {
      const errorMessage = error.message || "Ошибка при создании клиента";
      enqueueSnackbar(errorMessage, {
        variant: "error",
        autoHideDuration: 4000,
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
    }
  };

  return (
    <div className="AgencyClientsPage" style={{ width: "100%", height: "95%" }}>
      <div
        className="AgencyClientsPage-header"
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <div>
          <h2>Клиенты</h2>
        </div>
        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          <TextField
            size="small"
            fullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Поиск по ID, email, телефону или имени"
            sx={{ width: 400 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button variant="contained" onClick={() => setOpenModal(true)}>
            Добавить клиента
          </Button>
        </div>
      </div>
      <div style={{ width: "100%", overflow: "auto", height: "80%" }}>
        {isLoading ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <p>Загрузка...</p>
          </div>
        ) : clients.length === 0 ? (
          <div
            style={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "rgba(0,0,0,0.6)",
              fontSize: 16,
            }}
          >
            У вас пока нет клиентов.
          </div>
        ) : (
          <DataTable data={tableData} columns={columns} />
        )}
      </div>
      <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 16 }}>
        <button
          style={{
            padding: "6px 16px",
            borderRadius: 6,
            border: "none",
            background: currentPage === 1 ? "#e0e0e0" : "#dfe8ff",
            color: currentPage === 1 ? "#888" : "#3f73f9",
            cursor: currentPage === 1 ? "not-allowed" : "pointer",
          }}
          onClick={() => handlePageChange("prev")}
          disabled={currentPage === 1}
        >
          Назад
        </button>
        <strong style={{ marginTop: 6 }}>
          {currentPage} / {totalPages}
        </strong>
        <button
          style={{
            padding: "6px 16px",
            borderRadius: 6,
            border: "none",
            background: currentPage === totalPages ? "#e0e0e0" : "#dfe8ff",
            color: currentPage === totalPages ? "#888" : "#3f73f9",
            cursor: currentPage === totalPages ? "not-allowed" : "pointer",
          }}
          onClick={() => handlePageChange("next")}
          disabled={currentPage === totalPages}
        >
          Дальше
        </button>
      </div>
      <Modal open={openModal} onClose={closeModal}>
        <Box className="AgencyClientsModal">
          <h3>Новый клиент</h3>
          <div className="AgencyClientsModal-form">
            <TextField
              label="Имя"
              value={newClient.first_name}
              onChange={(e) => updateField("first_name", e.target.value)}
              onBlur={() => handleBlur("first_name")}
              error={!!(formErrors.first_name && (formSubmitted || touchedFields.first_name || debouncedValues.first_name !== undefined))}
              helperText={(formErrors.first_name && (formSubmitted || touchedFields.first_name || debouncedValues.first_name !== undefined)) ? formErrors.first_name : ""}
              fullWidth
            />
            <TextField
              label="Фамилия"
              value={newClient.surname}
              onChange={(e) => updateField("surname", e.target.value)}
              onBlur={() => handleBlur("surname")}
              error={!!(formErrors.surname && (formSubmitted || touchedFields.surname || debouncedValues.surname !== undefined))}
              helperText={(formErrors.surname && (formSubmitted || touchedFields.surname || debouncedValues.surname !== undefined)) ? formErrors.surname : ""}
              fullWidth
            />
            <TextField
              label="Почта"
              value={newClient.email}
              onChange={(e) => updateField("email", e.target.value)}
              onBlur={() => handleBlur("email")}
              error={!!(formErrors.email && (formSubmitted || touchedFields.email || debouncedValues.email !== undefined))}
              helperText={(formErrors.email && (formSubmitted || touchedFields.email || debouncedValues.email !== undefined)) ? formErrors.email : ""}
              fullWidth
              placeholder="client@example.ru"
            />
            <TextField
              label="Номер телефона"
              value={newClient.phone_number}
              onChange={(e) => updateField("phone_number", formatRussianPhone(e.target.value))}
              onBlur={() => handleBlur("phone_number")}
              error={!!(formErrors.phone_number && (formSubmitted || touchedFields.phone_number || debouncedValues.phone_number !== undefined))}
              helperText={(formErrors.phone_number && (formSubmitted || touchedFields.phone_number || debouncedValues.phone_number !== undefined)) ? formErrors.phone_number : ""}
              fullWidth
              placeholder="+7 (___) ___-__-__"
            />
            <TextField
              label="ИНН"
              value={newClient.inn}
              onChange={(e) => updateField("inn", cleanDigits(e.target.value).slice(0, 12))}
              onBlur={() => handleBlur("inn")}
              error={!!(formErrors.inn && (formSubmitted || touchedFields.inn || debouncedValues.inn !== undefined))}
              helperText={(formErrors.inn && (formSubmitted || touchedFields.inn || debouncedValues.inn !== undefined)) ? formErrors.inn : ""}
              fullWidth
              placeholder="10 или 12 цифр"
            />
          </div>
          <div className="AgencyClientsModal-actions">
            <Button variant="outlined" onClick={closeModal}>
              Отмена
            </Button>
            <Button variant="contained" onClick={handleCreateClient}>
              Создать
            </Button>
          </div>
        </Box>
      </Modal>
      <Modal open={successModalOpen} onClose={() => setSuccessModalOpen(false)}>
        <Box className="AgencyClientsModal AgencyClientsModal--success">
          <h3>Клиент создан</h3>
          <p className="AgencyClientsModal-note">
            Передайте данные клиенту, чтобы он вошел в личный кабинет.
          </p>
          {createdClientInfo && (
            <div className="AgencyClientsModal-summary">
              <div className="summary-row">
                <span className="summary-label">Ссылка входа:</span>
                <div className="summary-value">
                  <span style={{ color: "#000", userSelect: "text", wordBreak: "break-all", cursor: "pointer" }}>
                    {createdClientInfo.link}
                  </span>
                  <Tooltip title="Скопировать ссылку">
                    <IconButton size="small" onClick={() => {
                      navigator.clipboard.writeText(createdClientInfo.link);
                      setCopiedFields((prev) => ({ ...prev, link: true }));
                      setTimeout(() => {
                        setCopiedFields((prev) => ({ ...prev, link: false }));
                      }, 1500);
                    }}>
                      {copiedFields.link ? <CheckCircle fontSize="inherit" style={{ color: "#4caf50" }} /> : <ContentCopy fontSize="inherit" />}
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
              <div className="summary-row">
                <span className="summary-label">Почта:</span>
                <div className="summary-value">
                  {createdClientInfo.email}
                  <Tooltip title="Скопировать почту">
                    <IconButton size="small" onClick={() => {
                      navigator.clipboard.writeText(createdClientInfo.email);
                      setCopiedFields((prev) => ({ ...prev, email: true }));
                      setTimeout(() => {
                        setCopiedFields((prev) => ({ ...prev, email: false }));
                      }, 1500);
                    }}>
                      {copiedFields.email ? <CheckCircle fontSize="inherit" style={{ color: "#4caf50" }} /> : <ContentCopy fontSize="inherit" />}
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
              <div className="summary-row">
                <span className="summary-label">Пароль:</span>
                <div className="summary-value">
                  {createdClientInfo.password}
                  <Tooltip title="Скопировать пароль">
                    <IconButton size="small" onClick={() => {
                      navigator.clipboard.writeText(createdClientInfo.password);
                      setCopiedFields((prev) => ({ ...prev, password: true }));
                      setTimeout(() => {
                        setCopiedFields((prev) => ({ ...prev, password: false }));
                      }, 1500);
                    }}>
                      {copiedFields.password ? <CheckCircle fontSize="inherit" style={{ color: "#4caf50" }} /> : <ContentCopy fontSize="inherit" />}
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            </div>
          )}
          <div className="AgencyClientsModal-actions">
            <Button variant="contained" onClick={() => setSuccessModalOpen(false)}>
              Понятно
            </Button>
          </div>
        </Box>
      </Modal>
      <Modal open={contractsModal} onClose={handleCloseContracts}>
        <Box className="AgencyClientsModal AgencyClientsModal--contracts">
          <div className="AgencyClientsModal-header">
            <h3>Договоры клиента</h3>
            {contractsClient && (
              <p className="AgencyClientsModal-note">
                Клиент: {contractsClient.full_name || contractsClient.email} ({contractsClient.email})
              </p>
            )}
          </div>
          <div className="AgencyClientsModal-body">
            {contractsLoading ? (
              <p className="AgencyClientsModal-note">Загрузка...</p>
            ) : contracts.length === 0 ? (
              <p className="AgencyClientsModal-note">Договоры не найдены.</p>
            ) : (
              <div className="AgencyClientsContracts-tableWrap">
                <table className="AgencyClientsContracts-table">
                  <thead>
                    <tr>
                      <th>Номер</th>
                      <th>Дата заключения</th>
                      <th>Тип</th>
                      <th>Сумма</th>
                      <th>НДС</th>
                      <th>Дата передачи</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contracts.map((contract) => (
                      <tr key={contract.id}>
                        <td title={contract.contract_number || undefined}>
                          {contract.contract_number || "—"}
                        </td>
                        <td>{formatContractDate(contract.conclusion_date)}</td>
                        <td>
                          {CONTRACT_TYPE_LABELS[contract.contract_type] || contract.contract_type}
                        </td>
                        <td className="AgencyClientsContracts-table-amount">
                          {formatContractAmount(contract.amount)}
                        </td>
                        <td>
                          <span
                            className={`AgencyClientsContracts-vat ${
                              contract.with_vat
                                ? "AgencyClientsContracts-vat--with"
                                : "AgencyClientsContracts-vat--without"
                            }`}
                          >
                            {contract.with_vat ? "С НДС" : "Без НДС"}
                          </span>
                        </td>
                        <td>{formatContractDateTime(contract.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="AgencyClientsModal-actions">
            <Button variant="contained" onClick={handleCloseContracts}>
              Закрыть
            </Button>
          </div>
        </Box>
      </Modal>
      <Modal open={transferModal} onClose={handleCloseTransfer}>
        <Box className="AgencyClientsModal">
          <h3>Пополнение баланса клиента</h3>
          {currentClient && (
            <>
              <p className="AgencyClientsModal-note">
                Клиент: {currentClient.full_name || currentClient.email} ({currentClient.email})
              </p>
              <p className="AgencyClientsModal-note">
                Текущий баланс клиента: {((currentClient.deposits_amount || 0)).toLocaleString("ru-RU")} ₽
              </p>
              <p className="AgencyClientsModal-note">
                Ваш доступный баланс: {agencyBalance.toLocaleString("ru-RU")} ₽
              </p>
            </>
          )}
          <div className="AgencyClientsModal-form">
            <TextField
              label="Сумма пополнения"
              value={transferAmount}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                setTransferAmount(value);
                setTransferError("");
              }}
              error={!!transferError}
              helperText={transferError || "Введите сумму в рублях"}
              fullWidth
              placeholder="Введите сумму"
            />
          </div>
          <div className="AgencyClientsModal-actions">
            <Button variant="outlined" onClick={handleCloseTransfer}>
              Отмена
            </Button>
            <Button variant="contained" onClick={handleTransfer}>
              Пополнить
            </Button>
          </div>
        </Box>
      </Modal>
    </div>
  );
}

export default AgencyClients;

